/**
 * DocumentTemplatesPage duck — Push 8 Phase A.2c-1.
 *
 * Manages the document templates collection. Used by both DocumentTemplatesPage
 * (list view) and EditTemplatePage (single-template view) — the editor page
 * picks its target template by ID from the same collection that the list view
 * loads, so we don't need a per-template fetch.
 *
 * Phase A.2c-1: load / create / update / archive thunks. A.2c-2 reuses the
 * update thunk for editor content saves.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import {
  listTemplates as listTemplatesApi,
  createTemplate as createTemplateApi,
  getTemplate as getTemplateApi,
  updateTemplate as updateTemplateApi,
  archiveTemplate as archiveTemplateApi,
  restoreTemplate as restoreTemplateApi,
  forkTemplate as forkTemplateApi,
} from '../../util/api';

// ================ Async Thunks ================ //

// loadDocumentTemplates — fetches the active or archived set per the
// slice's current viewMode. The thunk reads viewMode from state via
// getState() so callers don't have to plumb it through; this keeps the
// route loader's signature simple (loadData() with no args).
const loadDocumentTemplatesPayloadCreator = async (_, { getState, rejectWithValue }) => {
  try {
    const viewMode = getState()?.DocumentTemplatesPage?.viewMode;
    const response = await listTemplatesApi({ archived: viewMode === 'archived' });
    return response.templates || [];
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};
export const loadDocumentTemplatesThunk = createAsyncThunk(
  'app/DocumentTemplatesPage/loadAll',
  loadDocumentTemplatesPayloadCreator
);
export const loadDocumentTemplates = () => dispatch =>
  dispatch(loadDocumentTemplatesThunk()).unwrap();

// createDocumentTemplate
const createDocumentTemplatePayloadCreator = async (body, { rejectWithValue }) => {
  try {
    const response = await createTemplateApi(body);
    return response.template;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};
export const createDocumentTemplateThunk = createAsyncThunk(
  'app/DocumentTemplatesPage/create',
  createDocumentTemplatePayloadCreator
);
export const createDocumentTemplate = body => dispatch =>
  dispatch(createDocumentTemplateThunk(body)).unwrap();

// loadTemplateForEdit — single-template fetch for the editor view. Returns
// { template, latestVersion } where latestVersion contains the editable
// content_html + content_json. The list-templates endpoint stays
// header-only for performance, so the editor uses this dedicated endpoint
// (added in A.2c-2 alongside the editor view).
const loadTemplateForEditPayloadCreator = async (id, { rejectWithValue }) => {
  try {
    const response = await getTemplateApi(id);
    return response;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};
export const loadTemplateForEditThunk = createAsyncThunk(
  'app/DocumentTemplatesPage/loadTemplateForEdit',
  loadTemplateForEditPayloadCreator
);
export const loadTemplateForEdit = id => dispatch =>
  dispatch(loadTemplateForEditThunk(id)).unwrap();

// updateDocumentTemplate — body must include `id`; remaining fields are optional.
// Returns the updated template row from the API.
const updateDocumentTemplatePayloadCreator = async (body, { rejectWithValue }) => {
  // Both content_json and custom_parameters are JSON.stringify'd before
  // transit serialization. The browser bundle of sharetribe-flex-sdk's
  // transit (in verbose mode) nullifies nested plain-object values;
  // encoding either field as a JSON string sidesteps that. The server
  // parses both back before validation (see server/api/update-template.js)
  // and src/util/api.js's serialize() for the broader caution. The typeof
  // checks avoid double-encoding if a caller ever passes a pre-stringified
  // value.
  let sendBody = body;
  if (sendBody && sendBody.content_json !== undefined) {
    sendBody = {
      ...sendBody,
      content_json:
        typeof sendBody.content_json === 'string'
          ? sendBody.content_json
          : JSON.stringify(sendBody.content_json),
    };
  }
  if (sendBody && sendBody.custom_parameters !== undefined) {
    sendBody = {
      ...sendBody,
      custom_parameters:
        typeof sendBody.custom_parameters === 'string'
          ? sendBody.custom_parameters
          : JSON.stringify(sendBody.custom_parameters),
    };
  }
  // Phase A.4a: same stringify branch for required_field_overrides. The
  // {[mergeFieldKey]: boolean} map is a plain-object payload and would be
  // nullified by the browser bundle's transit verbose-mode quirk if sent
  // as raw JS. Defense-in-depth typeof guard against double-encoding if
  // any future caller pre-stringifies.
  if (sendBody && sendBody.required_field_overrides !== undefined) {
    sendBody = {
      ...sendBody,
      required_field_overrides:
        typeof sendBody.required_field_overrides === 'string'
          ? sendBody.required_field_overrides
          : JSON.stringify(sendBody.required_field_overrides),
    };
  }

  try {
    const response = await updateTemplateApi(sendBody);
    // Return the FULL response shape ({template, version, contentVersionBumped})
    // — the reducer needs both `template` for the row's metadata patch AND
    // `version` to keep editTemplateContent in sync after content saves.
    // Phase A.3.3 redesign: ManageCustomFieldsModal's chip-count scan
    // reads editTemplateContent.content_json; without this sync the count
    // stays stale until the next loadTemplateForEdit.
    return response;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};
export const updateDocumentTemplateThunk = createAsyncThunk(
  'app/DocumentTemplatesPage/update',
  updateDocumentTemplatePayloadCreator
);
export const updateDocumentTemplate = body => dispatch =>
  dispatch(updateDocumentTemplateThunk(body)).unwrap();

// archiveDocumentTemplate — soft-delete via the backend's archive-template
// endpoint. The reducer drops the row from the collection on fulfilled.
const archiveDocumentTemplatePayloadCreator = async (id, { rejectWithValue }) => {
  try {
    await archiveTemplateApi(id);
    return id;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};
export const archiveDocumentTemplateThunk = createAsyncThunk(
  'app/DocumentTemplatesPage/archive',
  archiveDocumentTemplatePayloadCreator
);
export const archiveDocumentTemplate = id => dispatch =>
  dispatch(archiveDocumentTemplateThunk(id)).unwrap();

// loadProviderListingsForTemplateSettings — Push 8 Phase A.3.2.
// TemplateSettingsPage needs the provider's listings to render the
// per-listing assignment grid. We don't reuse ManageListingsPage's
// queryOwnListings thunk because that one writes into a different slice
// (state.ManageListingsPage.ownEntities) — pulling its data into the
// settings page would couple two pages' Redux shapes and risk the kind
// of stale-pagination cross-pollution the codebase otherwise avoids.
// Instead this thunk projects the SDK response into a simpler shape
// (id/title/state/listingType) and stores it in our own slice field.
//
// perPage: 100 cap mirrors server/api/update-template.js's ownership
// validation. Tahoe Waves runs at ~6-10 listings; 100 is comfortable
// headroom for V1. If a provider exceeds this the UI flags it.
const PROVIDER_LISTINGS_PER_PAGE = 100;

const loadProviderListingsForTemplateSettingsPayloadCreator = (
  _,
  { extra: sdk, rejectWithValue }
) => {
  return sdk.ownListings
    .query({ perPage: PROVIDER_LISTINGS_PER_PAGE })
    .then(response => {
      const rows = response?.data?.data || [];
      return rows
        .map(l => ({
          id: l?.id?.uuid,
          title: l?.attributes?.title || '',
          state: l?.attributes?.state || 'published',
          listingType: l?.attributes?.publicData?.listingType || null,
        }))
        .filter(r => !!r.id);
    })
    .catch(e => rejectWithValue(storableError(e)));
};
export const loadProviderListingsForTemplateSettingsThunk = createAsyncThunk(
  'app/DocumentTemplatesPage/loadProviderListingsForTemplateSettings',
  loadProviderListingsForTemplateSettingsPayloadCreator
);
export const loadProviderListingsForTemplateSettings = () => dispatch =>
  dispatch(loadProviderListingsForTemplateSettingsThunk()).unwrap();

// forkDocumentTemplate — Push 9 Sub-phase B polish (replaces the prior
// duplicateDocumentTemplate). Clones the source template via the
// backend's /api/fork-template endpoint with caller-supplied overrides
// for name + document_type + signer_role. Returns the new template id;
// the caller (DocumentTemplatesPage's modal-submit handler) navigates
// to /account/document-templates/<newId>/edit to drop the provider into
// the editor for the inactive clone.
//
// Thunk arg shape: { templateId, newName, newDocumentType, newSignerRole }.
// The pure-duplicate semantic (no overrides) corresponds to the caller
// leaving the modal defaults unchanged.
//
// The reducer does NOT optimistically insert the fork — the caller
// navigates to the editor on success and the next list re-mount
// refetches via loadDocumentTemplates. Avoids carrying a partial row
// (no current_version, no content_json) in state.
const forkDocumentTemplatePayloadCreator = async (args, { rejectWithValue }) => {
  try {
    const { templateId, newName, newDocumentType, newSignerRole } = args || {};
    const response = await forkTemplateApi({
      templateId,
      newName,
      newDocumentType,
      newSignerRole,
    });
    return response.newTemplateId;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};
export const forkDocumentTemplateThunk = createAsyncThunk(
  'app/DocumentTemplatesPage/fork',
  forkDocumentTemplatePayloadCreator
);
export const forkDocumentTemplate = args => dispatch =>
  dispatch(forkDocumentTemplateThunk(args)).unwrap();

// restoreDocumentTemplate — undo a soft-delete via the backend's
// restore-template endpoint. Mirrors archive's non-optimistic shape: the
// row is removed from the current (archived) list only on .fulfilled, not
// on .pending — so a failed restore leaves the user looking at the same
// archived list they started with. Returns the full restored template
// row so the reducer can capture it (currently unused beyond the id-based
// removal, but available if A.3+ wants it).
const restoreDocumentTemplatePayloadCreator = async (id, { rejectWithValue }) => {
  try {
    const response = await restoreTemplateApi(id);
    return response.template;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};
export const restoreDocumentTemplateThunk = createAsyncThunk(
  'app/DocumentTemplatesPage/restore',
  restoreDocumentTemplatePayloadCreator
);
export const restoreDocumentTemplate = id => dispatch =>
  dispatch(restoreDocumentTemplateThunk(id)).unwrap();

// ================ Slice ================ //

const slice = createSlice({
  name: 'DocumentTemplatesPage',
  initialState: {
    templates: [],
    loading: false,
    loadError: null,
    creating: false,
    createError: null,
    // Per-row in-flight tracking so the list-view can disable individual
    // rows during their own update/archive/restore without locking the
    // whole page.
    updatingIds: [],
    archivingIds: [],
    restoringIds: [],
    // Push 9 Sub-phase B polish — per-row in-flight tracking for the
    // Fork row action (replaces the prior `duplicatingIds`). Source row
    // stays in the list while the fork is being created; the action
    // button on that row disables to prevent double-submits. The reducer
    // doesn't optimistically add the new template — the caller navigates
    // to the editor on success and the next list mount refetches.
    forkingIds: [],
    /**
     * Which set of templates the list view is currently showing —
     * 'active' (default) or 'archived'. Source of truth for the
     * Active/Archived toggle. Read by loadDocumentTemplatesPayloadCreator
     * to choose between active and archived list endpoints.
     *
     * Persisted in the slice (not localStorage) so it survives intra-
     * session navigation (e.g., toggle to Archived → enter editor → back
     * to list still shows Archived). Resets to 'active' on hard refresh,
     * which is the desired default behavior.
     */
    viewMode: 'active',
    // Generic error for update/archive failures (rendered as a banner above
    // the list). Per-row errors aren't surfaced separately in A.2c-1 — the
    // failed row stays in place and the user can retry.
    error: null,
    // Set on createDocumentTemplate.fulfilled so the page can redirect to the
    // editor route. Cleared by the page after the redirect fires.
    lastCreatedId: null,
    // EditTemplatePage's currently-loaded template + latest content row.
    // loadTemplateForEdit.pending wipes both so a new load doesn't render
    // stale content from the previous template (template-A → template-B
    // navigation, or any soft-mount). loadTemplateForEdit.fulfilled fills
    // both. Update success patches editTemplate in place.
    editTemplate: null,
    editTemplateContent: null,
    editTemplateLoading: false,
    editTemplateError: null,
    // TemplateSettingsPage's provider-listings cache (Phase A.3.2). Wiped
    // on .pending so a stale set can't briefly show under the new page.
    // Refetched on every Settings page mount via the route's loadData.
    providerListings: [],
    loadProviderListingsInProgress: false,
    loadProviderListingsError: null,
  },
  reducers: {
    clearLastCreated: state => {
      state.lastCreatedId = null;
    },
    clearError: state => {
      state.error = null;
      state.createError = null;
      state.loadError = null;
      state.editTemplateError = null;
    },
    /**
     * Switch the active/archived view mode. Side effect of triggering a
     * fresh load lives in the calling page (setViewMode dispatched →
     * loadDocumentTemplates dispatched), keeping the reducer pure. The
     * templates array is wiped here so a stale set can't briefly show
     * under the new toggle label.
     */
    setViewMode: (state, action) => {
      const next = action.payload;
      if (next !== 'active' && next !== 'archived') return;
      if (state.viewMode === next) return;
      state.viewMode = next;
      state.templates = [];
      state.loadError = null;
    },
  },
  extraReducers: builder => {
    builder
      // load
      .addCase(loadDocumentTemplatesThunk.pending, state => {
        state.loading = true;
        state.loadError = null;
      })
      .addCase(loadDocumentTemplatesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(loadDocumentTemplatesThunk.rejected, (state, action) => {
        state.loading = false;
        state.loadError = action.payload;
      })

      // create
      .addCase(createDocumentTemplateThunk.pending, state => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createDocumentTemplateThunk.fulfilled, (state, action) => {
        state.creating = false;
        // Newest first — matches the backend's listTemplates ORDER BY desc.
        state.templates.unshift(action.payload);
        state.lastCreatedId = action.payload.id;
      })
      .addCase(createDocumentTemplateThunk.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.payload;
      })

      // load template for edit (single-template fetch with content)
      .addCase(loadTemplateForEditThunk.pending, state => {
        // Clear previous values so a stale template can't flash while the
        // new one is in flight (template-A → template-B navigation).
        state.editTemplate = null;
        state.editTemplateContent = null;
        state.editTemplateLoading = true;
        state.editTemplateError = null;
      })
      .addCase(loadTemplateForEditThunk.fulfilled, (state, action) => {
        state.editTemplateLoading = false;
        state.editTemplate = action.payload.template;
        state.editTemplateContent = action.payload.latestVersion;
        // Also keep the templates collection fresh if the row is already there.
        const idx = state.templates.findIndex(t => t.id === action.payload.template.id);
        if (idx >= 0) state.templates[idx] = action.payload.template;
      })
      .addCase(loadTemplateForEditThunk.rejected, (state, action) => {
        state.editTemplateLoading = false;
        state.editTemplateError = action.payload;
      })

      // update
      .addCase(updateDocumentTemplateThunk.pending, (state, action) => {
        const id = action.meta.arg.id;
        if (id && !state.updatingIds.includes(id)) state.updatingIds.push(id);
      })
      .addCase(updateDocumentTemplateThunk.fulfilled, (state, action) => {
        // Payload shape from updated thunk: { template, version, contentVersionBumped }.
        // Backward-compat: if any caller still returns just the template,
        // fall back to treating the payload itself as the template row.
        const payload = action.payload || {};
        const template = payload.template || payload;
        const version = payload.version;
        state.updatingIds = state.updatingIds.filter(id => id !== template.id);
        const idx = state.templates.findIndex(t => t.id === template.id);
        if (idx >= 0) state.templates[idx] = template;
        // Patch editTemplate too so the editor header reflects the update
        // (e.g., name change via Rename modal). Also patch editTemplateContent
        // when a `version` row came back so the chip-count scan in
        // ManageCustomFieldsModal sees the latest content_json without
        // requiring a fresh loadTemplateForEdit roundtrip.
        if (state.editTemplate && state.editTemplate.id === template.id) {
          state.editTemplate = template;
          if (version) {
            state.editTemplateContent = version;
          }
        }
      })
      .addCase(updateDocumentTemplateThunk.rejected, (state, action) => {
        const id = action.meta.arg.id;
        state.updatingIds = state.updatingIds.filter(x => x !== id);
        state.error = action.payload;
      })

      // archive
      .addCase(archiveDocumentTemplateThunk.pending, (state, action) => {
        const id = action.meta.arg;
        if (!state.archivingIds.includes(id)) state.archivingIds.push(id);
      })
      .addCase(archiveDocumentTemplateThunk.fulfilled, (state, action) => {
        const archivedId = action.payload;
        state.archivingIds = state.archivingIds.filter(id => id !== archivedId);
        state.templates = state.templates.filter(t => t.id !== archivedId);
      })
      .addCase(archiveDocumentTemplateThunk.rejected, (state, action) => {
        const id = action.meta.arg;
        state.archivingIds = state.archivingIds.filter(x => x !== id);
        state.error = action.payload;
      })

      // restore — non-optimistic mirror of archive: remove from current
      // (archived) list only on .fulfilled. The restored row drops from
      // the archived view because it's no longer archived; if the user
      // toggles to active, loadData will fetch the active set including
      // the restored row.
      //
      // Also patches editTemplate when the restored row is the one
      // currently loaded in EditTemplatePage. This supports the
      // "stay on page after restore" flow: deleted_at flips to null in
      // place, isArchived derives false on next render, and the editor
      // remounts in editable mode (the page bumps editorKey on success).
      // editTemplateContent (content_html / content_json) is unchanged
      // by restore — the row's metadata flipped but the content didn't —
      // so no patch needed there.
      .addCase(restoreDocumentTemplateThunk.pending, (state, action) => {
        const id = action.meta.arg;
        if (!state.restoringIds.includes(id)) state.restoringIds.push(id);
      })
      .addCase(restoreDocumentTemplateThunk.fulfilled, (state, action) => {
        const restored = action.payload;
        state.restoringIds = state.restoringIds.filter(id => id !== restored.id);
        state.templates = state.templates.filter(t => t.id !== restored.id);
        if (state.editTemplate && state.editTemplate.id === restored.id) {
          state.editTemplate = restored;
        }
      })
      .addCase(restoreDocumentTemplateThunk.rejected, (state, action) => {
        const id = action.meta.arg;
        state.restoringIds = state.restoringIds.filter(x => x !== id);
        state.error = action.payload;
      })

      // fork (Push 9 Sub-phase B polish — replaces prior duplicate cases)
      .addCase(forkDocumentTemplateThunk.pending, (state, action) => {
        const sourceId = action.meta.arg?.templateId;
        if (sourceId && !state.forkingIds.includes(sourceId)) {
          state.forkingIds.push(sourceId);
        }
      })
      .addCase(forkDocumentTemplateThunk.fulfilled, (state, action) => {
        // action.meta.arg is the full args object; the new id is
        // action.payload. Drop the source id from the in-flight set; do
        // NOT touch the templates collection — caller navigates to the
        // editor and a refetch happens on the next list mount.
        const sourceId = action.meta.arg?.templateId;
        state.forkingIds = state.forkingIds.filter(id => id !== sourceId);
      })
      .addCase(forkDocumentTemplateThunk.rejected, (state, action) => {
        const sourceId = action.meta.arg?.templateId;
        state.forkingIds = state.forkingIds.filter(id => id !== sourceId);
        state.error = action.payload;
      })

      // load provider listings (TemplateSettingsPage)
      .addCase(loadProviderListingsForTemplateSettingsThunk.pending, state => {
        // Wipe on pending so a previous provider's cached set can't flash
        // before the fresh fetch completes. Cheap — A.3.2 caps at 100.
        state.providerListings = [];
        state.loadProviderListingsInProgress = true;
        state.loadProviderListingsError = null;
      })
      .addCase(loadProviderListingsForTemplateSettingsThunk.fulfilled, (state, action) => {
        state.loadProviderListingsInProgress = false;
        state.providerListings = action.payload;
      })
      .addCase(loadProviderListingsForTemplateSettingsThunk.rejected, (state, action) => {
        state.loadProviderListingsInProgress = false;
        state.loadProviderListingsError = action.payload;
      });
  },
});

export const { clearLastCreated, clearError, setViewMode } = slice.actions;
export default slice.reducer;

// ================ Load data ================ //

// DocumentTemplatesPage (list view) — loads the full templates collection.
export const loadData = () => dispatch => dispatch(loadDocumentTemplatesThunk());

// EditTemplatePage (single-template editor) — loads the target template +
// its latest content version row by URL :id param. Keeps the list endpoint
// content-free for performance (templates can have 50KB+ HTML each).
export const loadEditTemplateData = params => dispatch => {
  const id = params?.id;
  if (!id) return Promise.resolve(null);
  return dispatch(loadTemplateForEditThunk(id));
};

// TemplateSettingsPage — loads the target template (same fetch as
// EditTemplatePage) AND the provider's listings in parallel. Both must
// resolve before the settings UI can render: the template gives us the
// current auto_attach + listing_ids; the listings give us the checkbox
// grid options.
export const loadTemplateSettingsData = params => dispatch => {
  const id = params?.id;
  if (!id) return Promise.resolve(null);
  return Promise.all([
    dispatch(loadTemplateForEditThunk(id)),
    dispatch(loadProviderListingsForTemplateSettingsThunk()),
  ]);
};
